import {
  buildCarePlanGeniePreview,
  CarePlanGenieFieldDefinition,
  CarePlanGenieQuestion,
} from "./carePlanGenieShared";

export interface LeftAloneGenieSectionValues {
  leftAloneDetails?: string;
  leftAloneAssistanceDetails?: string;
}

export const LEFT_ALONE_GENIE_FIELD_DEFINITIONS: Record<string, CarePlanGenieFieldDefinition> = {
  leftAloneDetails: { key: "leftAloneDetails", label: "Left alone details", type: "text", applyMode: "append" },
  leftAloneAssistanceDetails: { key: "leftAloneAssistanceDetails", label: "Left alone assistance details", type: "text", applyMode: "append" },
};

export const LEFT_ALONE_GENIE_QUESTIONS: CarePlanGenieQuestion[] = [
  {
    id: "supervision",
    title: "Supervision",
    prompt: "How should the resident's ability to be left alone be described?",
    helperText: "Choose details and caregiver interventions that fit best. Genie will append them into the section notes.",
    editableFieldKeys: ["leftAloneDetails", "leftAloneAssistanceDetails"],
    options: [
      {
        id: "left_short_period",
        label: "Can be left alone for short periods",
        description: "Needs check-ins but does not require constant direct supervision.",
        groupLabel: "Left alone details",
        updates: {
          leftAloneDetails: "Resident may be left alone for short periods when condition is stable and basic needs have been addressed beforehand.",
        },
      },
      {
        id: "left_needs_frequent_checks",
        label: "Needs frequent safety checks",
        description: "Time alone should be limited and monitored closely.",
        groupLabel: "Left alone details",
        updates: {
          leftAloneDetails: "Resident's time alone should be limited because ongoing monitoring is needed for safety and wellbeing.",
        },
      },
      {
        id: "left_not_safe",
        label: "Not safe to leave alone",
        description: "Requires direct supervision or near-constant support.",
        groupLabel: "Left alone details",
        updates: {
          leftAloneDetails: "Resident is not considered safe to be left alone due to current care, safety, or cognitive concerns.",
        },
      },
      {
        id: "left_confusion_risk",
        label: "Confusion or poor judgment affects time alone",
        description: "Cognitive status may limit safe unsupervised time.",
        groupLabel: "Left alone details",
        updates: {
          leftAloneDetails: "Resident may become confused, forget safety steps, or show poor judgment when alone, which limits how long unsupervised time is safe.",
        },
      },
      {
        id: "left_behavior_risk",
        label: "Behavior, wandering, or anxiety increases risk",
        description: "Time alone may be affected by emotional or behavioral symptoms.",
        groupLabel: "Left alone details",
        updates: {
          leftAloneDetails: "Behavioral symptoms, wandering risk, anxiety, or poor safety awareness may increase risk when the resident is left alone.",
        },
      },
      {
        id: "left_precheck",
        label: "Confirm safety and needed items before leaving",
        description: "Check comfort, call access, and essentials first.",
        groupLabel: "Caregiver interventions",
        updates: {
          leftAloneAssistanceDetails: "Caregiver should confirm safety, comfort, toileting needs, and access to important items before leaving the resident unattended.",
        },
      },
      {
        id: "left_frequent_checks",
        label: "Perform scheduled check-ins",
        description: "Use time-based checks when the resident can only be alone briefly.",
        groupLabel: "Caregiver interventions",
        updates: {
          leftAloneAssistanceDetails: "Caregiver should perform scheduled check-ins and adjust supervision based on the resident's current condition, risks, and tolerance for being alone.",
        },
      },
      {
        id: "left_emergency_plan",
        label: "Keep an emergency response plan available",
        description: "Resident and caregiver should know how help will be accessed.",
        groupLabel: "Caregiver interventions",
        updates: {
          leftAloneAssistanceDetails: "Caregiver should make sure the resident has a clear plan for calling for help and can access emergency support when unattended.",
        },
      },
      {
        id: "left_direct_supervision",
        label: "Maintain direct supervision when risk is high",
        description: "Use when the resident should not be left alone at all.",
        groupLabel: "Caregiver interventions",
        updates: {
          leftAloneAssistanceDetails: "Caregiver should maintain direct supervision or ensure another responsible support person is present when the resident needs ongoing observation.",
        },
      },
    ],
  },
];

export const buildLeftAloneGeniePreview = (
  currentValues: Record<string, any>,
  selectedOptions: Record<string, string[]>,
  questionDrafts: Record<string, Record<string, string>>,
) => buildCarePlanGeniePreview(
  LEFT_ALONE_GENIE_FIELD_DEFINITIONS,
  LEFT_ALONE_GENIE_QUESTIONS,
  currentValues,
  selectedOptions,
  questionDrafts,
);
